// The line chart Reporting's Over Time section draws (owner, 2026-11-04).
//
// Hand-drawn SVG, as the bar charts are hand-drawn divs: one line, oldest period
// on the left, a marker on every point with its value above it while there is
// room, a hover title per point, the value axis on the left from zero, and the
// periods along the bottom, thinned to what fits. It fills its container's
// width and keeps a fixed height.
//
// A point with no value (a handling-time median nobody measured) is not drawn,
// and the line breaks there rather than joining its neighbours across the gap.
import { useLayoutEffect, useRef, useState } from 'react'
import { makeStyles, tokens } from '@fluentui/react-components'
import type { OverTimePoint } from '../lib/overTimeCharts'

const HEIGHT = 320
const MARGIN = { top: 28, right: 28, bottom: 36, left: 64 }
/** Before the container has been measured (and in a test, which has no layout). */
const FALLBACK_WIDTH = 720
/** The room one axis label needs; closer than this, labels are skipped. */
const TICK_SPACE = 64
/** The room a value above a marker needs; closer than this, values are left to the hover title. */
const VALUE_SPACE = 44

const useStyles = makeStyles({
  root: { width: '100%', minWidth: 0 },
  svg: { display: 'block', overflow: 'visible' },
  grid: { stroke: tokens.colorNeutralStroke2, strokeWidth: 1 },
  axis: { stroke: tokens.colorNeutralStroke1, strokeWidth: 1 },
  axisText: { fill: tokens.colorNeutralForeground3, fontSize: '11px', fontFamily: tokens.fontFamilyBase },
  valueText: { fill: tokens.colorNeutralForeground2, fontSize: '11px', fontFamily: tokens.fontFamilyBase },
  marker: { stroke: tokens.colorNeutralBackground1, strokeWidth: 1.5 },
  hit: { fill: 'transparent', cursor: 'default' },
})

/**
 * The value axis: zero to a round number at or above `max`, in four or five
 * steps of 1, 2 or 5 times a power of ten. A count axis never shows a fraction.
 */
function niceTicks(max: number, wholeOnly: boolean): number[] {
  if (!(max > 0)) return [0, 1]
  const rough = max / 4
  const power = 10 ** Math.floor(Math.log10(rough))
  const step = [1, 2, 5, 10].map((m) => m * power).find((s) => s >= rough) ?? 10 * power
  const usable = wholeOnly ? Math.max(1, Math.ceil(step)) : step
  const ticks: number[] = []
  for (let v = 0; v < max + usable; v += usable) {
    ticks.push(Number(v.toPrecision(12)))
    if (v >= max) break
  }
  return ticks
}

export function OverTimeChart({
  points,
  color,
  axisFormat,
  wholeOnly,
  ariaLabel,
}: {
  points: readonly OverTimePoint[]
  color: string
  /** The value axis's labels: `$12K`, `2m 05s`, `40`. */
  axisFormat: (value: number) => string
  /** True for a count, whose axis steps by whole numbers. */
  wholeOnly: boolean
  ariaLabel: string
}) {
  const s = useStyles()
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(FALLBACK_WIDTH)
  useLayoutEffect(() => {
    const el = ref.current
    if (el === null) return
    const measure = () => setWidth(Math.max(320, Math.floor(el.getBoundingClientRect().width)))
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const plotW = width - MARGIN.left - MARGIN.right
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom
  const values = points.map((p) => p.value).filter((v): v is number => v !== null)
  const ticks = niceTicks(Math.max(0, ...values), wholeOnly)
  const top = ticks[ticks.length - 1] as number
  const n = points.length
  const gap = n > 1 ? plotW / (n - 1) : plotW
  const xAt = (i: number) => MARGIN.left + (n > 1 ? i * gap : plotW / 2)
  const yAt = (v: number) => MARGIN.top + plotH - (top === 0 ? 0 : (v / top) * plotH)

  // The line, broken at a missing value.
  let path = ''
  let open = false
  points.forEach((p, i) => {
    if (p.value === null) {
      open = false
      return
    }
    path += `${open ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(p.value).toFixed(1)}`
    open = true
  })

  const every = Math.max(1, Math.ceil(TICK_SPACE / Math.max(gap, 1)))
  const showValues = gap >= VALUE_SPACE || n === 1

  return (
    <div ref={ref} className={s.root}>
      <svg className={s.svg} width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} role="img" aria-label={ariaLabel}>
        {ticks.map((t) => (
          <g key={t}>
            <line className={s.grid} x1={MARGIN.left} x2={width - MARGIN.right} y1={yAt(t)} y2={yAt(t)} />
            <text className={s.axisText} x={MARGIN.left - 8} y={yAt(t)} textAnchor="end" dominantBaseline="middle">
              {axisFormat(t)}
            </text>
          </g>
        ))}
        <line className={s.axis} x1={MARGIN.left} x2={width - MARGIN.right} y1={yAt(0)} y2={yAt(0)} />
        {/* Counted back from the newest, so the latest period is always named. */}
        {points.map((p, i) =>
          (n - 1 - i) % every === 0 ? (
            <text key={p.key} className={s.axisText} x={xAt(i)} y={HEIGHT - MARGIN.bottom + 18} textAnchor="middle">
              {p.tick}
            </text>
          ) : null,
        )}
        <path d={path} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) =>
          p.value === null ? null : (
            <g key={p.key}>
              <circle className={s.marker} cx={xAt(i)} cy={yAt(p.value)} r={4} fill={color} />
              {showValues && (
                <text className={s.valueText} x={xAt(i)} y={yAt(p.value) - 10} textAnchor="middle">
                  {p.display}
                </text>
              )}
              {/* A wider, invisible target, so the hover title is easy to find. */}
              <circle className={s.hit} cx={xAt(i)} cy={yAt(p.value)} r={Math.max(6, Math.min(12, gap / 2))}>
                <title>{p.title}</title>
              </circle>
            </g>
          ),
        )}
      </svg>
    </div>
  )
}
